import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import classnames from 'classnames';
import Portal from '../../wui-overlay/src/Portal';
import i18n from './i18n';
import { getLangInfo } from '../../wui-locale/src/tool';
import { Warning, debounce } from '../../wui-core/src';
import { SpinProps } from './iSpin';
import {WebUI} from '../../wui-core/src/index';
import {WithConfigConsumer, ConfigContext} from "../../wui-provider/src/context";
import loadingSvg from './images/loading.svg';


const { isShouldUpdate } = Warning;

const defaultProps = {
    loadingType: 'default',
    clsLoadBack: '',
    color: '',
    showBackDrop: true,
    fullScreen: false,
    show: false,
    wrapperClassName: '',
    tip: '',
    delay: null,
    antd: false,
    backDropClassName: ''
};

function shouldDelay(spinning?: boolean, delay?: number | null) {
    return !!spinning && !!delay && !isNaN(Number(delay));
}

// 优化的防抖 hook，避免不必要的依赖变化
// function useDebouncedEffect(effect: () => void, deps: any[], delay?: number) {
//     const effectRef = useRef(effect);

//     // 只在effect真正变化时更新，避免每次渲染都更新
//     useEffect(() => {
//         effectRef.current = effect;
//     });

//     useEffect(() => {
//         if (delay == null) {
//             effectRef.current();
//             return;
//         }
//         // const handler = setTimeout(() => effectRef.current(), delay);
//         // return () => clearTimeout(handler);
//         const handler = debounce(() => effectRef.current(), delay);
//         return () => {
//             handler.cancel();
//         };
//     }, [...deps, delay]);
// }

const LoadingComponent: React.FC<SpinProps> = (props) => {
    // 优化props解构，避免每次创建新对象，直接使用默认值
    const {
        clsPrefix,
        loadingType = defaultProps.loadingType,
        size,
        color,
        show = defaultProps.show,
        spinning,
        showBackDrop = defaultProps.showBackDrop,
        container,
        getPopupContainer,
        children,
        fullScreen = defaultProps.fullScreen,
        wrapperClassName = defaultProps.wrapperClassName,
        indicator,
        className,
        tip = defaultProps.tip,
        antd = defaultProps.antd,
        backDropClassName = defaultProps.backDropClassName,
        dir,
        delay = defaultProps.delay,
        ...others
    } = props;

    const context = useContext(ConfigContext) // 接受provider控制
    const actualLocale = props.locale || context.locale || 'zh-cn';

    // 简化状态初始化，避免不必要的函数创建
    const [spinStatus, setSpinStatus] = useState(spinning && !shouldDelay(spinning, delay));
    const [hasDidMount, setHasDidMount] = useState(false);
    const spinKeyRef = useRef(null);


    let updateSpinning = useCallback(() => {
        if (!!spinning !== spinStatus) {
            setSpinStatus(!!spinning);
        }
    }, [spinning, spinStatus])

    const cancelExistingSpin = useCallback(() => {
        if (updateSpinning && (updateSpinning as any).cancel) {
            (updateSpinning as any).cancel();
        }
    }, [updateSpinning]);

    let realupdateSpinningRef = useRef<any>(null);

    const debouncifyUpdateSpinning = useCallback((PropDelay?: any) => {
        const realDelay = PropDelay || delay;
        if (realDelay) {
            cancelExistingSpin();
            realupdateSpinningRef.current = debounce(updateSpinning, realDelay);
        } else {
            realupdateSpinningRef.current = updateSpinning;
        }
    }, [cancelExistingSpin, delay, updateSpinning])

    useEffect(() => {
        setHasDidMount(true);
    }, [])


    useEffect(() => {
        debouncifyUpdateSpinning();
        realupdateSpinningRef.current?.();
        return () => {
            cancelExistingSpin();
        }
    }, [debouncifyUpdateSpinning, realupdateSpinningRef.current, cancelExistingSpin, delay])

    // spinning 状态防抖
    // const safeDelay = typeof delay === 'number' && !isNaN(delay) ? delay : undefined;
    // useDebouncedEffect(() => {
    //     setSpinStatus(!!spinning);
    // }, [spinning], safeDelay);

    // 挂载标记
    // useEffect(() => {
    //     setHasDidMount(true);
    // }, []);

    // 优化容器DOM获取，使用useMemo缓存结果
    const containerDom = useMemo(() => {
        if (getPopupContainer && typeof getPopupContainer === 'function' && spinKeyRef.current) {
            return () => getPopupContainer(spinKeyRef.current as any);
        }
        return getPopupContainer || container;
    }, [getPopupContainer, container]);

    // 缓存语言配置
    const local = useMemo(() => getLangInfo(actualLocale!, i18n, 'spin'), [actualLocale]);

    useEffect(() => {
        if (containerDom && spinKeyRef.current) {
            let portalContainerNode = (typeof containerDom === 'function' ? containerDom(spinKeyRef.current) : containerDom) as HTMLElement
            if (portalContainerNode?.className && portalContainerNode !== document.body && portalContainerNode.className.indexOf(`${clsPrefix}-container`) == -1) {
                portalContainerNode.className += ` ${clsPrefix}-container`
            }
        }
    }, [containerDom])

    isShouldUpdate('Spin', props);

    // 缓存复杂的布尔计算，避免每次重新计算
    const computedValues = useMemo(() => ({
        isShow: props.hasOwnProperty('spinning') ? spinStatus : show,
        hasAntdChildren: typeof children !== 'undefined' && (loadingType === 'antd' || antd),
        hasAntdNoChildren: typeof children === 'undefined' && (loadingType === 'antd' || antd)
    }), [spinStatus, show, children, loadingType, antd, props]);

    const { isShow, hasAntdChildren, hasAntdNoChildren } = computedValues;

    // 计算 className
    const classes = useMemo(() => {
        const clsObj = classnames(clsPrefix, {
            [`${className}`]: !!className,
            [`${clsPrefix}-${loadingType}`]: true,
            [`${clsPrefix}-${loadingType}-rtl`]: dir === 'rtl',
            [`${clsPrefix}-${loadingType}-sm`]: size === 'sm',
            [`${clsPrefix}-${loadingType}-lg`]: size === 'lg',
            [`${clsPrefix}-${loadingType}-${color}`]: !!color,
            [`${clsPrefix}-show-text`]: !!tip,
            [wrapperClassName as string]: loadingType !== 'antd' && !antd
        });
        return clsObj;
    }, [clsPrefix, loadingType, dir, size, color, tip, wrapperClassName, antd, className]);

    const antdClsObj = useMemo(() => classnames({ [wrapperClassName as string]: hasAntdNoChildren }), [wrapperClassName, hasAntdNoChildren]);

    // 使用useMemo缓存DOM渲染逻辑，避免每次重新创建复杂的DOM结构
    const renderContent = useMemo(() => {
        if ((loadingType === 'custom' && !!indicator) || loadingType === 'rotate') {
            const loadDom = loadingType === 'custom' ? indicator : <img src={loadingSvg} />;
            return (
                <div className={antdClsObj}>
                    <div className={classes} {...others}>
                        <div className={`${clsPrefix}-spin`}>{loadDom}</div>
                        {tip ? <div className={`${clsPrefix}-desc`}>{tip}</div> : null}
                    </div>
                    {!tip && children && <div className={`${clsPrefix}-desc`}>{children}</div>}
                </div>
            );
        } else if (loadingType === 'default' || loadingType === 'antd') {
            let defaultTip = tip ? tip : local.langMap.loading;
            return (
                <div className={`${clsPrefix}-default-container ${antdClsObj}`}>
                    <div className={classes} {...others}>
                        <div className={`${clsPrefix}-spin ${clsPrefix}-dot ${clsPrefix}-dot-spin`}>
                            <i></i>
                            <i></i>
                            <i></i>
                            <i></i>
                        </div>
                    </div>
                    <div className={`${clsPrefix}-desc`}>{defaultTip}</div>
                </div>
            );
        } else if (loadingType === 'line') {
            return (
                <div className={antdClsObj}>
                    <div className={classes} {...others}>
                        <div />
                        <div />
                        <div />
                        <div />
                        <div />
                    </div>
                    {tip ? <p className={`${clsPrefix}-desc`}>{tip}</p> : null}
                    {!tip && children && <div className={`${clsPrefix}-desc`}>{children}</div>}
                </div>
            );
        } else if (loadingType === 'cricle') {
            return <div className={`${classes} ${antdClsObj}`} {...others} />;
        }
        return null;
    }, [loadingType, indicator, antdClsObj, classes, clsPrefix, tip, children, local.langMap.loading, others]);

    let dom = renderContent;

    // 遮罩层
    const backClassObj = useMemo(() => ({
        [`${clsPrefix}-backdrop`]: true,
        [`${clsPrefix}-full-screen`]: fullScreen,
        [backDropClassName as string]: backDropClassName
    }), [clsPrefix, fullScreen, backDropClassName]);

    if (showBackDrop) {
        dom = (
            <div className={classnames(backClassObj)} style={{ zIndex: isShow ? 1900 : 'auto' }} ref={spinKeyRef}>
                {dom}
            </div>
        );
    }

    if (hasAntdChildren) {
        const containerClassName = classnames(`${clsPrefix}-container`, {
            [`${clsPrefix}-blur`]: isShow
        });
        dom = (
            <div className={`${clsPrefix}-nested-loading ${wrapperClassName}`} ref={spinKeyRef}>
                {isShow && <>{dom}</>}
                <div className={containerClassName} key="container">
                    {children}
                </div>
            </div>
        );
        if (typeof containerDom === 'undefined') {
            return <>{dom}</>;
        }
    }

    return !isShow && !hasAntdChildren ? null : typeof window !== 'undefined' && hasDidMount ? (
        <Portal container={containerDom as any}>{dom}</Portal>
    ) : (
        <>{dom}</>
    );
};

// 使用React.memo优化组件，避免不必要的重渲染
const Loading = React.memo(LoadingComponent, (prevProps, nextProps) => {
    // 自定义比较逻辑，只比较关键props
    const keysToCompare: (keyof SpinProps)[] = [
        'spinning', 'show', 'loadingType', 'size', 'color', 'tip',
        'fullScreen', 'showBackDrop', 'delay', 'children', 'indicator',
        'container', 'getPopupContainer', 'className', 'wrapperClassName',
        'backDropClassName', 'locale', 'antd', 'dir'
    ];

    return keysToCompare.every(key => prevProps[key] === nextProps[key]);
});

export default WithConfigConsumer({name: 'spin'})(WebUI({name: 'spin', defaultProps})(Loading));
